import {useRef, useState} from 'react';
import {ImagePlus, Rotate3d, Sparkles, Star, Trash2} from 'lucide-react';
import {looksEquirectangular, putImage, useMediaSrc} from '../lib/media';
import {Pano360} from '../pano/Pano360';
import {newId} from '../store/StoreContext';
import {SCENE_LABEL, type Photo, type SceneId} from '../store/types';
import {Button, Select} from '../ui/primitives';
import {useToast} from '../ui/Toast';
import {Picture} from './Picture';

const SCENES = Object.keys(SCENE_LABEL) as SceneId[];

function PanoPreview({photo}: {photo: Photo}) {
  const src = useMediaSrc(photo.ref, '360');
  if (!src) {
    return (
      <div className="pano-stage flex items-center justify-center rounded-control text-[12px] font-semibold text-white/80" style={{aspectRatio: '16 / 9'}}>
        Đang dựng ảnh 360…
      </div>
    );
  }
  return (
    <div style={{aspectRatio: '16 / 9'}}>
      <Pano360 src={src} label={photo.label} />
    </div>
  );
}

function PhotoTile({
  isCover,
  onLabel,
  onRemove,
  onSetCover,
  photo,
}: {
  isCover: boolean;
  onLabel: (label: string) => void;
  onRemove: () => void;
  onSetCover: () => void;
  photo: Photo;
}) {
  const [previewing, setPreviewing] = useState(false);

  return (
    <li className="surface-sm rounded-card p-3">
      {photo.kind === '360' && previewing ? (
        <PanoPreview photo={photo} />
      ) : (
        <div className="relative">
          <Picture alt={photo.label} className="rounded-control" ratio="4 / 3" refItem={photo.ref} />
          {photo.kind === '360' ? (
            <span className="photo-tag absolute left-2 top-2 flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold">
              <Rotate3d size={11} /> 360°
            </span>
          ) : null}
          {isCover ? (
            <span className="photo-tag absolute right-2 top-2 flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold">
              <Star size={11} /> Ảnh bìa
            </span>
          ) : null}
        </div>
      )}

      <input
        value={photo.label}
        onChange={(event) => onLabel(event.target.value)}
        placeholder="Chú thích (Phòng khách…)"
        className="control mt-3 min-h-10 w-full rounded-control px-3 text-[13px]"
      />

      <div className="mt-2 flex flex-wrap gap-2">
        {photo.kind === '360' ? (
          <Button onClick={() => setPreviewing((prev) => !prev)}>
            {previewing ? 'Đóng xem thử' : 'Xem thử 360'}
          </Button>
        ) : (
          <Button disabled={isCover} onClick={onSetCover}>
            Đặt làm ảnh bìa
          </Button>
        )}
        <Button tone="danger" icon={<Trash2 size={14} />} onClick={onRemove}>
          Xoá
        </Button>
      </div>
    </li>
  );
}

/**
 * The picture desk of a listing.
 *
 * A 360 photo is not "a bigger photo": it must be equirectangular, so the
 * uploader checks the shape and says so rather than letting the seller publish a
 * tour that looks broken to every buyer. Sellers with no 360 camera can still
 * try the feature end to end with a drawn sample room — the same code path an
 * uploaded file takes.
 */
export function PhotoManager({
  coverId,
  onChange,
  photos,
}: {
  coverId: string | null;
  onChange: (photos: Photo[], coverId: string | null) => void;
  photos: Photo[];
}) {
  const {notify} = useToast();
  const flatInput = useRef<HTMLInputElement>(null);
  const panoInput = useRef<HTMLInputElement>(null);
  const [scene, setScene] = useState<SceneId>('phong-khach');
  const [busy, setBusy] = useState(false);

  const flat = photos.filter((photo) => photo.kind === 'anh');
  const tours = photos.filter((photo) => photo.kind === '360');

  const add = (next: Photo[]) => {
    const all = [...photos, ...next];
    onChange(all, coverId ?? all.find((photo) => photo.kind === 'anh')?.id ?? all[0]?.id ?? null);
  };

  const upload = async (files: FileList | null, kind: Photo['kind']) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const added: Photo[] = [];
      for (const file of Array.from(files)) {
        const result = await putImage(file);
        if (kind === '360' && !looksEquirectangular(result.width, result.height)) {
          notify('Ảnh 360 nên có tỉ lệ 2:1 (ví dụ 4096×2048) để không bị méo', 'warn');
        }
        added.push({
          id: newId('ph'),
          kind,
          label: kind === '360' ? 'Ảnh 360' : '',
          ref: result.ref,
        });
      }
      add(added);
      notify(`Đã thêm ${added.length} ảnh`);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Không tải được ảnh lên', 'warn');
    } finally {
      setBusy(false);
    }
  };

  const update = (id: string, patch: Partial<Photo>) => {
    onChange(
      photos.map((photo) => (photo.id === id ? {...photo, ...patch} : photo)),
      coverId,
    );
  };

  const remove = (id: string) => {
    const next = photos.filter((photo) => photo.id !== id);
    onChange(next, coverId === id ? (next[0]?.id ?? null) : coverId);
  };

  return (
    <div className="space-y-4">
      <section>
        <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-[13px] font-bold text-ink">Ảnh thường ({flat.length})</h3>
          <div className="flex gap-2">
            <Button
              icon={<Sparkles size={15} />}
              onClick={() => {
                const next = SCENES[flat.length % SCENES.length]!;
                add([
                  {
                    id: newId('ph'),
                    kind: 'anh',
                    label: SCENE_LABEL[next],
                    ref: {scene: next, seed: newId('seed'), source: 'art'},
                  },
                ]);
              }}
            >
              Ảnh mẫu
            </Button>
            <Button
              icon={<ImagePlus size={15} />}
              disabled={busy}
              onClick={() => flatInput.current?.click()}
            >
              Tải ảnh lên
            </Button>
          </div>
        </header>
        <input
          ref={flatInput}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(event) => {
            void upload(event.target.files, 'anh');
            event.target.value = '';
          }}
        />
        {flat.length === 0 ? (
          <p className="sunken-sm rounded-card p-4 text-[12.5px] text-ink-3">
            Tin có ảnh được xem nhiều hơn hẳn. Ảnh đầu tiên sẽ là ảnh bìa.
          </p>
        ) : (
          <ul className="grid gap-3 @lg:grid-cols-2">
            {flat.map((photo) => (
              <PhotoTile
                key={photo.id}
                photo={photo}
                isCover={photo.id === coverId}
                onLabel={(label) => update(photo.id, {label})}
                onRemove={() => remove(photo.id)}
                onSetCover={() => onChange(photos, photo.id)}
              />
            ))}
          </ul>
        )}
      </section>

      <section>
        <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-[13px] font-bold text-ink">
            <Rotate3d size={16} className="text-accent" /> Ảnh 360° ({tours.length})
          </h3>
          <Button
            icon={<ImagePlus size={15} />}
            disabled={busy}
            onClick={() => panoInput.current?.click()}
          >
            Tải ảnh 360
          </Button>
        </header>
        <input
          ref={panoInput}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(event) => {
            void upload(event.target.files, '360');
            event.target.value = '';
          }}
        />

        <p className="sunken-sm rounded-card p-3 text-[12.5px] text-ink-3">
          Ảnh 360 là ảnh phẳng dạng <strong className="font-semibold text-ink-2">equirectangular</strong>{' '}
          tỉ lệ 2:1 — xuất từ máy Insta360 / Ricoh Theta hoặc chế độ Photo Sphere trên điện thoại.
        </p>

        {tours.length > 0 ? (
          <ul className="mt-3 grid gap-3 @lg:grid-cols-2">
            {tours.map((photo) => (
              <PhotoTile
                key={photo.id}
                photo={photo}
                isCover={photo.id === coverId}
                onLabel={(label) => update(photo.id, {label})}
                onRemove={() => remove(photo.id)}
                onSetCover={() => onChange(photos, photo.id)}
              />
            ))}
          </ul>
        ) : null}

        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[180px] flex-1">
            <span className="mb-1.5 block text-[12px] font-semibold text-ink-2">
              Chưa có máy 360? Thêm phòng mẫu
            </span>
            <Select
              value={scene}
              onChange={(value) => setScene(value as SceneId)}
              options={SCENES.map((value) => ({label: SCENE_LABEL[value], value}))}
            />
          </div>
          <Button
            icon={<Sparkles size={15} />}
            onClick={() =>
              add([
                {
                  id: newId('ph'),
                  kind: '360',
                  label: SCENE_LABEL[scene],
                  ref: {scene, seed: newId('seed'), source: 'art'},
                },
              ])
            }
          >
            Thêm ảnh 360 mẫu
          </Button>
        </div>
      </section>
    </div>
  );
}
